import { SkillAuditPanel } from '@/components/admin/skills/SkillAuditPanel';
import { SKILL_FORM_HTML_ID, SkillForm } from '@/components/admin/skills/SkillForm';
import { SkillPromptEvolution } from '@/components/admin/skills/SkillPromptEvolution';
import { PageHeader } from '@/components/layout/PageHeader';
import { PageShell } from '@/components/layout/PageShell';
import { Button } from '@/components/ui/button';
import { CopyButton } from '@/components/ui/copy-button';
import { ApiError, api } from '@/lib/api';
import { skillCallTemplate } from '@/lib/skill-call-template';
import type { CreateSkillRequest, UpdateSkillRequest } from '@innermate/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { ArrowLeft, Code2, Trash2, Type } from 'lucide-react';

interface AdminSkillDetailProps {
  id: string;
  prefillDataSourceId?: string;
}

export function AdminSkillDetail({ id, prefillDataSourceId }: AdminSkillDetailProps) {
  const isNew = id === 'new';
  return isNew ? <CreateView prefillDataSourceId={prefillDataSourceId} /> : <EditView id={id} />;
}

function CreateView({ prefillDataSourceId }: { prefillDataSourceId?: string }) {
  const navigate = useNavigate();
  const qc = useQueryClient();

  const dsQuery = useQuery({
    queryKey: ['admin', 'data-sources', prefillDataSourceId],
    queryFn: () => api.dataSourceGet(prefillDataSourceId ?? ''),
    enabled: !!prefillDataSourceId,
    // SkillForm prefill 한 번만 필요 — polling 불필요.
    staleTime: Number.POSITIVE_INFINITY,
    refetchInterval: false,
  });

  const mutation = useMutation({
    mutationFn: (body: CreateSkillRequest) => api.adminSkillCreate(body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin', 'skills'] });
      navigate({ to: '/admin/skills' });
    },
  });

  const serverError =
    mutation.error instanceof ApiError
      ? mutation.error.message
      : mutation.error
        ? 'Skill 생성에 실패했습니다.'
        : null;

  // dataSourceId 가 있으면 db.* tools + 화이트리스트 자동 prefill.
  const ds = dsQuery.data;
  const initial = prefillDataSourceId
    ? {
        // slug 는 사용자가 입력 — kebab 변환 시도
        slug:
          (ds?.name ?? '')
            .toLowerCase()
            .replace(/[^a-z0-9]+/g, '-')
            .replace(/^-|-$/g, '')
            .slice(0, 40) || '',
        name: ds ? `${ds.name} 분석` : '',
        description: ds?.description ?? null,
        systemPrompt: ds
          ? `당신은 "${ds.name}" 데이터셋을 분석해 사용자 질문에 답하는 어시스턴트입니다.\n\n${
              ds.description ? `데이터셋 설명: ${ds.description}\n\n` : ''
            }SQL (DuckDB 방언) 로 분석을 수행하세요. db.list_tables / db.describe_table 로 구조를 먼저 파악한 뒤 db.run_query 로 답을 만드세요.`
          : '',
        toolVisibility: 'summary' as const,
        enabled: true,
        tools: [
          { toolKey: 'db.list_tables' as const, config: { dataSourceIds: [prefillDataSourceId] } },
          {
            toolKey: 'db.describe_table' as const,
            config: { dataSourceIds: [prefillDataSourceId] },
          },
          {
            toolKey: 'db.run_query' as const,
            config: { dataSourceIds: [prefillDataSourceId], maxRows: 500 },
          },
        ],
      }
    : undefined;

  return (
    <PageShell
      header={
        <>
          <BackLink />
          <PageHeader
            title={prefillDataSourceId ? `Skill: ${ds?.name ?? '데이터셋'}` : '새 Skill'}
            description={
              prefillDataSourceId
                ? '데이터셋이 자동 연결되어 있습니다. 시스템 프롬프트와 이름을 다듬어 저장하세요.'
                : 'LLM 에 부착되는 시스템 프롬프트와 툴 번들을 정의합니다.'
            }
            actions={
              <Button type="submit" form={SKILL_FORM_HTML_ID} disabled={mutation.isPending}>
                {mutation.isPending ? '저장 중…' : 'Skill 만들기'}
              </Button>
            }
          />
        </>
      }
    >
      <SkillForm
        initial={initial}
        serverError={serverError}
        onSubmit={(body) => mutation.mutate(body)}
        showChangelog={false}
      />
    </PageShell>
  );
}

function EditView({ id }: { id: string }) {
  const navigate = useNavigate();
  const qc = useQueryClient();

  const skillQuery = useQuery({
    queryKey: ['admin', 'skills', id],
    queryFn: () => api.adminSkillGet(id),
  });

  const updateMutation = useMutation({
    mutationFn: (body: UpdateSkillRequest) => api.adminSkillUpdate(id, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin', 'skills'] });
      qc.invalidateQueries({ queryKey: ['admin', 'skills', id] });
      navigate({ to: '/admin/skills' });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: () => api.adminSkillDelete(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin', 'skills'] });
      navigate({ to: '/admin/skills' });
    },
  });

  if (skillQuery.isLoading) {
    return (
      <PageShell header={<BackLink />}>
        <p className="text-sm text-foreground-muted">불러오는 중…</p>
      </PageShell>
    );
  }
  if (skillQuery.error || !skillQuery.data) {
    return (
      <PageShell header={<BackLink />}>
        <p className="text-sm text-destructive">
          {skillQuery.error instanceof ApiError
            ? skillQuery.error.message
            : 'Skill 을 찾을 수 없습니다.'}
        </p>
      </PageShell>
    );
  }

  const skill = skillQuery.data;

  const serverError =
    updateMutation.error instanceof ApiError
      ? updateMutation.error.message
      : updateMutation.error
        ? '수정에 실패했습니다.'
        : null;

  return (
    <PageShell
      header={
        <>
          <BackLink />
          <PageHeader
            title={skill.name}
            titleSuffix={
              <span className="inline-flex items-center gap-0.5">
                <CopyButton icon={Type} text={skill.name} label="이름 복사" />
                <CopyButton
                  icon={Code2}
                  text={skillCallTemplate(skill.id)}
                  label="skill 함수 복사"
                />
              </span>
            }
            description={skill.slug}
            actions={
              <div className="flex items-center gap-2">
                <Button type="submit" form={SKILL_FORM_HTML_ID} disabled={updateMutation.isPending}>
                  {updateMutation.isPending ? '저장 중…' : '저장'}
                </Button>
                <Button
                  variant="ghost"
                  onClick={() => {
                    if (
                      window.confirm(
                        `Skill "${skill.name}" 을 삭제할까요?\n이 Skill 을 쓰던 세션은 skillId 가 해제됩니다.`,
                      )
                    ) {
                      deleteMutation.mutate();
                    }
                  }}
                  aria-label="삭제"
                >
                  <Trash2 className="size-4" />
                  삭제
                </Button>
              </div>
            }
          />
        </>
      }
    >
      <SkillForm
        initial={{
          slug: skill.slug,
          name: skill.name,
          description: skill.description,
          systemPrompt: skill.systemPrompt,
          toolVisibility: skill.toolVisibility,
          enabled: skill.enabled,
          tools: skill.tools,
          pairsWellWith: skill.pairsWellWith,
        }}
        slugLocked
        serverError={serverError}
        onSubmit={(body, opts) => {
          // slug 은 편집 불가 (서버에서도 omit) — 굳이 payload 에 넣지 않는다
          const { slug: _slug, ...rest } = body;
          updateMutation.mutate({
            ...rest,
            ...(opts.changelog ? { changelog: opts.changelog } : {}),
          });
        }}
        showChangelog
      />
      <SkillRolesPanel skillId={id} />
      <SkillAuditPanel skillId={id} />
      <SkillPromptEvolution skillId={id} />
    </PageShell>
  );
}

function SkillRolesPanel({ skillId }: { skillId: string }) {
  const { data, isLoading } = useQuery({
    queryKey: ['admin', 'skills', skillId, 'roles'],
    queryFn: () => api.adminSkillRoles(skillId),
  });
  return (
    <section className="mt-6 rounded-md border border-border bg-surface p-4">
      <h2 className="mb-2 text-sm font-semibold">이 Skill 을 부여한 역할 (RBAC)</h2>
      <p className="mb-3 text-xs text-foreground-muted">
        이 Skill 은 아래 역할에 매핑되어 있어, 해당 역할을 가진 사용자에게 자동으로 노출됩니다. 역할
        관리는 관리자 &rsaquo; 역할(RBAC) 페이지에서.
      </p>
      {isLoading ? (
        <p className="text-sm text-foreground-muted">불러오는 중…</p>
      ) : !data || data.roles.length === 0 ? (
        <p className="text-sm text-foreground-muted">
          이 Skill 을 부여한 역할이 없습니다. 사용자에게 직접 부여(사용자 편집) 한 경우만
          노출됩니다.
        </p>
      ) : (
        <ul className="space-y-1.5">
          {data.roles.map((r) => (
            <li key={r.id} className="flex items-center gap-2 text-sm">
              <span className="font-medium">{r.name}</span>
              {!r.enabled ? (
                <span className="rounded bg-surface-muted px-1.5 py-0.5 text-xs text-foreground-muted">
                  비활성
                </span>
              ) : null}
              {r.description ? (
                <span className="text-xs text-foreground-muted line-clamp-1">
                  — {r.description}
                </span>
              ) : null}
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

function BackLink() {
  const navigate = useNavigate();
  return (
    <button
      type="button"
      onClick={() => navigate({ to: '/admin/skills' })}
      className="mb-4 inline-flex items-center gap-1 text-xs text-foreground-muted hover:text-foreground"
    >
      <ArrowLeft className="size-3.5" />
      Skill 목록
    </button>
  );
}
